import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { PerformanceModeService } from '@core/performance/performance-mode.service';

/** Classe no painel do overlay enquanto a folha ou o diálogo sai (styles/plugins/cdk-dialog.scss) */
export const DIALOG_CLOSING_CLASS = 'agj-dialog-closing';

/** Rede de segurança se o animationend não vier (aba em segundo plano, painel fora da tela): saída de 240 ms + folga */
export const DIALOG_EXIT_FALLBACK_MS = 320;

/**
 * Saída animada de um diálogo do CDK. O DialogRef.close do CDK descarta o overlay na hora (painel e fundo somem
 * juntos); aqui o primeiro close marca o painel com a classe de saída, apaga o fundo pela transição dele e só
 * fecha de verdade no fim da animação. `closed` emite depois disso, com o mesmo resultado e a mesma origem de foco.
 * Fecha na hora sem movimento (modo desempenho, "Reduzir movimento"), com closePredicate (que pode recusar o
 * fechamento) e quando o painel já saiu da página (navegação descartou o overlay).
 */
export function animateDialogClose(ref: DialogRef<unknown, unknown>, motionOff: () => boolean): void {
  const close = ref.close.bind(ref);
  let closing = false;

  ref.close = (result?: unknown, options?: Parameters<DialogRef['close']>[1]): void => {
    if (closing) return;

    const pane = ref.overlayRef.overlayElement;
    const container = pane?.querySelector<HTMLElement>('.cdk-dialog-container');
    if (motionOff() || ref.config.closePredicate || !container?.isConnected) {
      close(result, options);
      return;
    }

    closing = true;
    pane.classList.add(DIALOG_CLOSING_CLASS);
    ref.overlayRef.backdropElement?.classList.remove('cdk-overlay-backdrop-showing');

    const onAnimationEnd = (event: AnimationEvent): void => {
      if (event.target === container) finish();
    };
    const fallback = setTimeout(finish, DIALOG_EXIT_FALLBACK_MS);
    container.addEventListener('animationend', onAnimationEnd);

    function finish(): void {
      clearTimeout(fallback);
      container?.removeEventListener('animationend', onAnimationEnd);
      close(result, options);
    }
  };
}

/**
 * Roda `action` com o resultado na hora em que o diálogo pede para fechar, ainda dentro do toque, antes da saída
 * animada (o `closed` só emite no fim dela). Para o que o navegador só permite no gesto do usuário: o Safari
 * bloqueia a aba nova (WhatsApp) aberta depois da animação. Chame logo depois de `dialog.open`.
 */
export function beforeDialogClose<R>(ref: DialogRef<R, unknown>, action: (result: R | undefined) => void): void {
  const close = ref.close.bind(ref);
  let notified = false;

  ref.close = (result?: R, options?: Parameters<DialogRef['close']>[1]): void => {
    if (!notified) {
      notified = true;
      action(result);
    }
    close(result, options);
  };
}

/** No inicializador do app: todo diálogo aberto pelo Dialog do CDK (também os do One) ganha a saída animada */
export function enableDialogExitAnimation(): void {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return;

  const performance = inject(PerformanceModeService);
  inject(Dialog).afterOpened.subscribe((ref) => animateDialogClose(ref, () => performance.motionOff()));
}
